import _createForOfIteratorHelper from "/builds/client-projects/fert-demolition-motos-website/application/node_modules/@babel/runtime/helpers/esm/createForOfIteratorHelper.js";
import _objectSpread from "/builds/client-projects/fert-demolition-motos-website/application/node_modules/@babel/runtime/helpers/esm/objectSpread2.js";
import { DELETED } from "./special.js";
import { normalize as arrayNormalize } from "./schemas/Array.js";
import { normalize as objectNormalize } from "./schemas/Object.js";

var visit = function visit(value, parent, key, schema, addEntity, visitedEntities) {
  if (!value || !schema) {
    return value;
  }

  if (schema.normalize && typeof schema.normalize === 'function') {
    if (typeof value !== 'object') return value;
    return schema.normalize(value, parent, key, visit, addEntity, visitedEntities);
  } // serializable


  if (typeof schema === 'function') {
    return new schema(value);
  }

  if (typeof value !== 'object' || typeof schema !== 'object') return value;
  var method = Array.isArray(schema) ? arrayNormalize : objectNormalize;
  return method(schema, value, parent, key, visit, addEntity, visitedEntities);
};

var addEntities = function addEntities(entities, indexes, existingEntities, existingIndexes, entityMeta, meta) {
  return function (schema, processedEntity, id) {
    var schemaKey = schema.key;

    if (!(schemaKey in entities)) {
      entities[schemaKey] = {};
      existingEntities[schemaKey] = _objectSpread({}, existingEntities[schemaKey]);
      entityMeta[schemaKey] = _objectSpread({}, entityMeta[schemaKey]);
    }

    var existingEntity = entities[schemaKey][id];

    if (existingEntity) {
      // TODO: maybe have distinct merge function for this case
      entities[schemaKey][id] = schema.merge(existingEntity, processedEntity);
    } else {
      // TODO: eventually assume this exists and don't check for conditional. probably early 2022
      var entityExpiresAt = schema.expiresAt ? schema.expiresAt(meta, processedEntity) : meta.expiresAt;
      var inStoreEntity = existingEntities[schemaKey][id];

      if (inStoreEntity) {
        var _entityMeta$schemaKey, _entityMeta$schemaKey2; // this case we already have this entity in store
        // if either of these is undefined, it resolves to 'false' which
        // means we fallback to 'newer' (processedEntity) takes priority


        var preferExisting = ((_entityMeta$schemaKey = entityMeta[schemaKey][id]) == null ? void 0 : _entityMeta$schemaKey.date) > meta.date;

        if (typeof processedEntity !== typeof inStoreEntity) {
          entities[schemaKey][id] = preferExisting ? inStoreEntity : processedEntity;
        } else {
          // second argument takes priority over first
          entities[schemaKey][id] = preferExisting ? schema.merge(processedEntity, inStoreEntity) : schema.merge(inStoreEntity, processedEntity);
        }

        entityMeta[schemaKey][id] = ((_entityMeta$schemaKey2 = entityMeta[schemaKey][id]) == null ? void 0 : _entityMeta$schemaKey2.expiresAt) >= entityExpiresAt ? entityMeta[schemaKey][id] : {
          expiresAt: entityExpiresAt,
          date: meta.date
        };
      } else {
        entities[schemaKey][id] = processedEntity;
        entityMeta[schemaKey][id] = {
          expiresAt: entityExpiresAt,
          date: meta.date
        };
      }
    } // update index


    if (Array.isArray(schema.indexes)) {
      var entity = entities[schemaKey][id];

      if (!(schemaKey in indexes)) {
        indexes[schemaKey] = {};
        existingIndexes[schemaKey] = _objectSpread({}, existingIndexes[schemaKey]);
      }

      var _iterator = _createForOfIteratorHelper(schema.indexes),
          _step;

      try {
        for (_iterator.s(); !(_step = _iterator.n()).done;) {
          var index = _step.value;

          if (!(index in indexes[schemaKey])) {
            existingIndexes[schemaKey][index] = indexes[schemaKey][index] = {};
          }

          var indexMap = indexes[schemaKey][index];

          if (existingEntity) {
            delete indexMap[existingEntity[index]];
          } // entity already in cache but the index changed


          if (existingEntities[schemaKey] && existingEntities[schemaKey][id] && existingEntities[schemaKey][id][index] !== entity[index]) {
            indexMap[existingEntities[schemaKey][id][index]] = DELETED;
          }

          if (index in entity) {
            indexMap[entity[index]] = id;
          }
          /* istanbul ignore next */
          else if ( // eslint-disable-next-line no-undef
          process.env.NODE_ENV !== 'production') {
            console.warn("Index not found in entity. Indexes must be top-level members of your entity.\nIndex: ".concat(index, "\nEntity: ").concat(JSON.stringify(entity, undefined, 2)));
          }
        }
      } catch (err) {
        _iterator.e(err);
      } finally {
        _iterator.f();
      }
    } // set this after index updates so we know what indexes to remove from


    existingEntities[schemaKey][id] = entities[schemaKey][id];
  };
};

function expectedSchemaType(schema) {
  return ['object', 'function'].includes(typeof schema) ? 'object' : typeof schema;
} // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types


export var normalize = function normalize(input, schema) {
  var existingEntities = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var existingIndexes = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  var existingEntityMeta = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {};
  var meta = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : {
    date: Date.now(),
    expiresAt: Infinity
  };
  // no schema means we don't process at all
  if (schema === undefined) return {
    entities: existingEntities,
    indexes: existingIndexes,
    result: input,
    entityMeta: existingEntityMeta
  };
  var schemaType = expectedSchemaType(schema);

  if (input === null || typeof input !== schemaType && !(schema.key !== undefined && typeof input === 'string')) {
    /* istanbul ignore else */
    if (process.env.NODE_ENV !== 'production') {
      var parseWorks = function parseWorks(input) {
        try {
          return typeof JSON.parse(input) !== 'string';
        } catch (e) {
          return false;
        }
      };

      if (typeof input === 'string' && parseWorks(input)) {
        throw new Error("Normalizing a string, but this does match schema.\n\nParsing this input string as JSON worked. This likely indicates fetch function did not parse\nthe JSON. By default, this only happens if \"content-type\" header includes \"json\".\nSee https://resthooks.io/docs/guides/custom-networking for more information\n\n  Schema: ".concat(JSON.stringify(schema, undefined, 2), "\n  Input: \"").concat(input, "\""));
      } else {
        throw new Error("Unexpected input given to normalize. Expected type to be \"".concat(schemaType, "\", found \"").concat(input === null ? 'null' : typeof input, "\".\n\n          Schema: ").concat(JSON.stringify(schema, undefined, 2), "\n          Input: \"").concat(input, "\""));
      }
    } else {
      throw new Error("Unexpected input given to normalize. Expected type to be \"".concat(schemaType, "\", found \"").concat(input === null ? 'null' : typeof input, "\"."));
    }
  }

  var newEntities = {};
  var newIndexes = {};

  var entities = _objectSpread({}, existingEntities);

  var indexes = _objectSpread({}, existingIndexes);

  var entityMeta = _objectSpread({}, existingEntityMeta);

  var addEntity = addEntities(newEntities, newIndexes, entities, indexes, entityMeta, meta);
  var visitedEntities = {};
  var result = visit(input, input, undefined, schema, addEntity, visitedEntities);
  return {
    entities: entities,
    indexes: indexes,
    result: result,
    entityMeta: entityMeta
  };
};